import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Typography,
    Box,
    Link,
    Tooltip,
} from "@mui/material";
import { Skeleton } from "@mui/material";
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    DeleteOutline as DeleteIcon,
    WarningAmber,
    InfoOutlined,
} from "@mui/icons-material";
import formatCurrency from "../../utilities/currency";
import {
    formatOrderQuantity,
    getMinimumSalesOrderQuantity,
    getMultipleSalesOrderQuantity,
    normalizeOrderQuantity,
} from "../../utilities/orderQuantity";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";

const getNormalizedProductId = (item) => item?.ProductID || item?.product_id;
const getNormalizedMachineId = (item) =>
    item?.MachineID ||
    item?.machine_id ||
    item?.machineId ||
    item?.product_data?.machine_id ||
    null;
const getCartSite = (item, fallbackSite = "") =>
    item?.Site || item?.site_number || fallbackSite;
const getCartLineKey = (productId, machineId) =>
    `${productId || ""}:${machineId || ""}`;

const CartTable = ({
    groupedCartProducts,
    cartItems,
    selectedSite,
    selectedSiteCurrency,
    maxQtyAllowed,
    quantityInputs,
    handleInputChange,
    handleInputBlur,
    handleKeyPress,
    handleKeyDown,
    handleQuantityChange,
    handleRemoveItem,
    parsePrice,
    handleNavigateToProduct,
    t,
}) => {
    const [showNote, setShowNote] = useState({});

    useEffect(() => {

    }, [groupedCartProducts])

    return (
        <TableContainer sx={{ borderRadius: '10px', overflow: 'hidden' }}>
            <Table>
                <TableHead sx={{ mt: 1 }}>
                    <TableRow>
                        <TableCell width={30} />
                        <TableCell>{t("TABLE_HEADERS.PARTS_QUANTITY")}</TableCell>
                        <TableCell align="center">{t("TABLE_HEADERS.QUANTITY")}</TableCell>
                        <TableCell align="right">{t("TABLE_HEADERS.PRICE")}</TableCell>
                        <TableCell align="right">{t("TABLE_HEADERS.TOTAL")}</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.keys(groupedCartProducts).map((ctpDate) => (
                        <React.Fragment key={ctpDate}>
                            <TableRow sx={{
                                "& td": {
                                    borderBottom: 0,
                                    padding: "10px"
                                },
                                "&:last-child td, &:last-child th": {
                                    border: 0,
                                },
                            }}>

                                <TableCell colSpan={6} sx={{ padding: '5px' }}>
                                    <Typography variant="body1">
                                        <span style={{ color: "green" }}>
                                            {t("UI_TEXT.ESTIMATED_SHIPPING_DATE")}
                                        </span>{" "}
                                        {ctpDate === '__loading__'
                                            ? <Skeleton variant="text" width={160} height={22} sx={{ display: 'inline-block' }} />
                                            : ctpDate}
                                    </Typography>
                                </TableCell>
                            </TableRow>


                            {groupedCartProducts[ctpDate].map((item) => {
                                const productData = item?.product_data || null
                                const machineNumber =
                                    productData?.machine_number ||
                                    productData?.machineNumber ||
                                    item?.machine_number ||
                                    item?.machineData?.machine_number ||
                                    "N/A";
                                const machineId = getNormalizedMachineId(item) || productData.machine_id;
                                const lineKey = getCartLineKey(productData.product_id, machineId);

                                const cartItem = cartItems.find(
                                    (ci) =>
                                        getNormalizedProductId(ci) === productData.product_id &&
                                        getCartSite(ci, selectedSite) === selectedSite &&
                                        (
                                            !machineId ||
                                            Number(getNormalizedMachineId(ci)) === Number(machineId)
                                        )
                                );

                                const currentQuantity = cartItem
                                    ? cartItem.Quantity
                                    : item.Quantity;
                                const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(productData);
                                const multipleSalesOrderQuantity = getMultipleSalesOrderQuantity(productData);
                                const minimumAllowedQuantity = normalizeOrderQuantity(1, productData);
                                const effectiveMaxQtyAllowed = Math.max(maxQtyAllowed, minimumSalesOrderQuantity, normalizeOrderQuantity(maxQtyAllowed, productData));

                                const hasOutOfStock = item?.ctp?.out_of_stock?.qty > 0;

                                return (
                                    <>
                                        <TableRow
                                            key={lineKey}
                                            sx={{
                                                ml: 1,
                                                "& td, & th": {
                                                    borderBottom: "1px solid rgba(224,224,224,1)", // only show border if NO note
                                                },
                                                "& td:first-of-type": {
                                                    borderBottom: "none", // remove border for first cell
                                                },
                                                "&:last-child td, &:last-child th": {
                                                    border: 0,
                                                },
                                            }}
                                        >
                                            {/* Product info */}
                                            <TableCell width={30} />
                                            <TableCell>
                                                <Box className="product-cell" sx={{ display: "flex", alignItems: "center" }}>
                                                    <img
                                                        src={item.imageSrc}
                                                        alt={productData.product_name}
                                                        className="product-image"
                                                        style={{ width: 40, height: 40, marginRight: 8 }}
                                                    />
                                                    <Box className="product-info" sx={{ overflow: "hidden" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Tooltip title={productData.product_name}>
                                                                <Link
                                                                    underline="hover"
                                                                    sx={{
                                                                        cursor: "pointer",
                                                                        maxWidth: 350,
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        display: "inline-block",
                                                                    }}
                                                                    onClick={() => {
                                                                        const url = `/machines/${productData.machine_id}/${productData.product_id}`;
                                                                        handleNavigateToProduct(url);
                                                                    }}
                                                                >
                                                                    {productData.product_name}
                                                                </Link>
                                                            </Tooltip>

                                                            {item.showDisclaimer && (
                                                                <WarningAmber color="warning" fontSize="small" />
                                                            )}
                                                        </Box>

                                                        <Tooltip title={productData.product_number}>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    maxWidth: 350,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    display: "block",
                                                                    fontSize: '11px'
                                                                }}
                                                            >
                                                                <b>{t("UI_TEXT.PART")}</b> {productData.product_number} |{" "}
                                                                <b>{t("UI_TEXT.MACHINE")}</b> {machineNumber}
                                                            </Typography>
                                                        </Tooltip>
                                                        <ReplacementPartNotice product={productData} />
                                                        {(minimumSalesOrderQuantity > 1 || multipleSalesOrderQuantity > 0) && (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "4px",
                                                                    flexWrap: "wrap",
                                                                }}
                                                            >
                                                                {minimumSalesOrderQuantity > 1 && (
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: "11px" }}
                                                                    >
                                                                        {t("UI_TEXT.MINIMUM_ORDER_QUANTITY", {
                                                                            defaultValue: "Minimum order quantity: {{quantity}}",
                                                                            quantity: formatOrderQuantity(minimumSalesOrderQuantity),
                                                                        })}
                                                                    </Typography>
                                                                )}

                                                                {minimumSalesOrderQuantity > 1 && multipleSalesOrderQuantity > 0 && (
                                                                    <Typography
                                                                        component="span"
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: "11px" }}
                                                                    >
                                                                        |
                                                                    </Typography>
                                                                )}

                                                                {multipleSalesOrderQuantity > 0 && (
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: "11px" }}
                                                                    >
                                                                        {t("UI_TEXT.MULTIPLE_ORDER_QUANTITY", {
                                                                            defaultValue: "Multiple order quantity: {{quantity}}",
                                                                            quantity: formatOrderQuantity(multipleSalesOrderQuantity),
                                                                        })}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>


                                            {/* Quantity controls */}
                                            <TableCell align="center">
                                                <Box className="quantity-controls">
                                                    <IconButton
                                                        size="small"
                                                        disabled={Number(currentQuantity) <= minimumAllowedQuantity}
                                                        onClick={() =>
                                                            handleQuantityChange(productData.product_id, machineId, -1)
                                                        }
                                                    >
                                                        <RemoveIcon />
                                                    </IconButton>
                                                    <TextField
                                                        type="text"
                                                        size="small"
                                                        value={
                                                            quantityInputs[lineKey] !==
                                                                undefined
                                                                ? quantityInputs[lineKey]
                                                                : currentQuantity.toString()
                                                        }
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                productData.product_id,
                                                                machineId,
                                                                e.target.value
                                                            )
                                                        }
                                                        onBlur={() => handleInputBlur(productData.product_id, machineId)}
                                                        onKeyPress={(e) =>
                                                            handleKeyPress(productData.product_id, machineId, e)
                                                        }
                                                        onKeyDown={(e) =>
                                                            handleKeyDown(productData.product_id, machineId, e)
                                                        }
                                                        className="quantity-input"
                                                        placeholder="Qty"
                                                        inputProps={{
                                                            style: { textAlign: "center" },
                                                            "aria-label": t('ARIA_LABELS.QUANTITY'),
                                                            inputMode: "decimal",
                                                            min: minimumAllowedQuantity,
                                                            maxLength: 6,
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        disabled={Number(currentQuantity) >= effectiveMaxQtyAllowed}
                                                        onClick={() =>
                                                            handleQuantityChange(productData.product_id, machineId, 1)
                                                        }
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>

                                            {/* Price */}
                                            <TableCell align="right">
                                                {formatCurrency(
                                                    parsePrice(
                                                        productData[`price_${selectedSiteCurrency}`]
                                                    ),
                                                    selectedSiteCurrency
                                                )}
                                            </TableCell>

                                            {/* Total */}
                                            <TableCell align="right">
                                                {formatCurrency(
                                                    parsePrice(
                                                        productData[`price_${selectedSiteCurrency}`]
                                                    ) * currentQuantity,
                                                    selectedSiteCurrency
                                                )}
                                            </TableCell>

                                            {/* Remove button */}
                                            <TableCell>
                                                <Tooltip title={t('UI_TEXT.REMOVE_PART_FROM_CART')} arrow placement="right">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            handleRemoveItem(productData.product_id, machineId)
                                                        }
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>

                                    </>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default CartTable;
